/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react';
import { useLocalStore } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { TranslationFileLocationDropDowns } from 'src/translation/components/TranslationFileLocationDropDowns';

const messages = defineMessages({
    modalTitle: { id: 'translation.translationUploadFileLocationModal.modalTitle', defaultMessage: 'Upload files location' },
    selectFoldersButton: { id: 'translation.translationUploadFileLocationModal.selectFoldersButton', defaultMessage: 'Select folder' },
    modalDescription: {
        id: 'translation.translationUploadFileLocationModal.modalDescription',
        defaultMessage: `By default, we store your uploaded files in a container and folder we create for you.
         You can select another blob container and folder to upload your data in`
    }
});

type TranslationUploadFileLocationModalProps = BasicModalProps<{}> & {
    selectedFolder: string;
    selectedStorageAccount: string;
    isStorageContainersLoading: boolean;
    selectedDestinationContainer: string;
    setSelectedFolder: (folder: string) => void;
    storageContainers: AzureBlobContainerStore[];
    setSelectedDestinationContainer: (container: string) => void;
};

export const TranslationUploadFileLocationModal = withModalMounter((props: TranslationUploadFileLocationModalProps) => {
    const {
        onDismiss,
        selectedFolder,
        storageContainers,
        setSelectedFolder,
        selectedStorageAccount,
        isStorageContainersLoading,
        selectedDestinationContainer,
        setSelectedDestinationContainer
    } = props;
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();

    const [chosenFolder, setChosenFolder] = React.useState<string>(selectedFolder);
    const [chosenContainer, setChosenContainer] = React.useState<string>(selectedDestinationContainer);

    const blobFilesTracker = useProgressTracker();
    const filesStoreHouse = useLocalStore<ResourceStoreHouse<AzureBlobStore>>(createResourceStoreHouse);

    React.useEffect(() => {
        if (chosenContainer) {
            dispatcher.dispatch('getAzureBlobsByContainerName', {
                toastData: {},
                tracker: blobFilesTracker,
                localStore: filesStoreHouse,
                storageAccountName: selectedStorageAccount,
                containerName: chosenContainer
            });
        }
    }, [chosenContainer]);

    const handleSubmit = () => {
        setSelectedDestinationContainer(chosenContainer);
        setSelectedFolder(chosenFolder);
        onDismiss();
    };

    return (
        <BasicModal
            {...props}
            valid
            onSubmit={handleSubmit}
            doneButtonText={localize(messages.selectFoldersButton)}
            dialogContentProps={{ title: localize(messages.modalTitle) }}>
            <Stack tokens={{ childrenGap: 4 }}>
                <LuisText>{localize(messages.modalDescription)}</LuisText>
                <TranslationFileLocationDropDowns
                    isHorizontalDropDowns={false}
                    onChangeStorageContainer={option => setChosenContainer(option)}
                    storageContainers={storageContainers}
                    selectedDestinationContainer={chosenContainer}
                    isStorageContainersLoading={isStorageContainersLoading}
                    storageAccountName={selectedStorageAccount}
                    selectedFolder={chosenFolder}
                    onChangeDestinationFolder={option => setChosenFolder(option)}
                    showFoldersDropDown
                    blobFilesTracker={blobFilesTracker}
                    blobFiles={filesStoreHouse.items}
                    isBlobFilesLoading={filesStoreHouse.tracker.isRunning}
                    isSelectFolderDisabled={!chosenContainer || isStorageContainersLoading}
                />
            </Stack>
        </BasicModal>
    );
});
